/**
 * ============================================================================
 *  APP · BROWSER ENTRY
 * ============================================================================
 *  Wires the app's sections to the framework, then renders.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

import { CACHE_TTL_MS } from '@framework/core';
import { requireSection } from '@framework/core/decorators';
import { compile } from '@framework/core/schema-gen';
import {
  AuthProvider, bootstrapCssVars, isPersistableKey, isPersistableEntry, applyFlagsFromUrl, watchFlagsInUrl,
  PERSISTED_CACHE_KEY, ErrorBoundary, installClientErrorReporting,
} from '@framework/react';

import '@app/sections';                       // registers every @Section — must come first
import { SectionName } from '@app/sections';
import './flags';                            // registers this site's feature flags
import { THEME_VARS } from './theme-vars';
import App from './App';
import './styles.css';

// Before anything else that can throw — and on this branch the very next line can:
// compile() and requireSection() run before React exists. A startup crash is exactly
// the one nobody is watching for, and the browser console is not somewhere we can read.
installClientErrorReporting();

// Palette from the annotation defaults, before React renders: no flash, and no
// duplicated colour literals in the stylesheet.
bootstrapCssVars(THEME_VARS, compile(requireSection(SectionName.Theme)).defaults);

// ?flag=<mask> on ANY page. Enable-only: never switches anything off.
applyFlagsFromUrl();
// And again whenever the hash changes: a flag link pasted into a tab that is already
// open changes the URL without reloading, and would otherwise be read by nobody.
watchFlagsInUrl();

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: true,
      staleTime: CACHE_TTL_MS,
      // Must outlive staleTime, or entries would be evicted exactly when they become
      // revalidatable — and the version we need to send would be gone.
      gcTime: 7 * 24 * 60 * 60 * 1000,
    },
  },
});

/**
 * Section cache survives a reload. Without it the cache is memory-only: a refresh
 * loses every version, so the client has nothing to put in the QUERY request and the
 * server must send full payloads for content that has not changed.
 */
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  // The framework owns this key: #/flush-all deletes it, and a flush that missed it
  // would be undone by the next reload.
  key: PERSISTED_CACHE_KEY,
});

createRoot(document.getElementById('app')!).render(
  <StrictMode>
    {/* Outermost, so a throw anywhere below shows a message instead of a blank page. */}
    <ErrorBoundary>
    <PersistQueryClientProvider
      client={qc}
      persistOptions={{
        persister,
        maxAge: 7 * 24 * 60 * 60 * 1000,
        // v2 discards caches written before denials were excluded — those poisoned
        // entries make every section render nothing.
        buster: 'v2',
        dehydrateOptions: {
          // Sections only. Sessions, permissions and admin lists are never written to
          // disk — stale identity on disk is precisely the wrong thing to cache.
          shouldDehydrateQuery: (q) =>
            isPersistableKey(q.queryKey) &&
            q.state.status === 'success' &&
            isPersistableEntry(q.state.data),
        },
      }}
    >
      <AuthProvider>
        <App />
      </AuthProvider>
    </PersistQueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
